import { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { FaChevronDown, FaCode, FaImages, FaLink } from "react-icons/fa";

const HOVER_PREVIEW_DELAY_MS = 160;

// Pick a concept figure from handout assets, a pasted URL, or new Python code.
// Only the item the pointer rests on is fetched, so skimming the list stays cheap.
export default function HandoutFocusImagePicker({ files, onPickAsset, onAddUrl, onAddCode, disabled = false }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const [previewFile, setPreviewFile] = useState(null);
  const [urlOpen, setUrlOpen] = useState(false);
  const [url, setUrl] = useState("");
  const rootRef = useRef(null);
  const listRef = useRef(null);
  const hoverTimerRef = useRef(null);

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return needle ? files.filter((file) => String(file.name || file.key).toLowerCase().includes(needle)) : files;
  }, [files, query]);

  useEffect(() => () => window.clearTimeout(hoverTimerRef.current), []);
  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => { if (!rootRef.current?.contains(event.target)) setOpen(false); };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, [open]);
  useEffect(() => {
    if (!open) { setQuery(""); setActiveIndex(-1); setPreviewFile(null); }
  }, [open]);

  const schedulePreview = (file) => {
    window.clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = window.setTimeout(() => setPreviewFile(file), HOVER_PREVIEW_DELAY_MS);
  };
  const pick = (file) => {
    if (!file) return;
    onPickAsset(file);
    setOpen(false);
  };
  const moveActive = (step) => {
    if (!matches.length) return;
    const next = (activeIndex + step + matches.length) % matches.length;
    setActiveIndex(next);
    setPreviewFile(matches[next]);
    listRef.current?.querySelector(`[data-index="${next}"]`)?.scrollIntoView?.({ block: "nearest" });
  };
  const handleKeyDown = (event) => {
    if (event.key === "ArrowDown") { event.preventDefault(); moveActive(1); }
    else if (event.key === "ArrowUp") { event.preventDefault(); moveActive(-1); }
    else if (event.key === "Enter") { event.preventDefault(); pick(matches[activeIndex]); }
    else if (event.key === "Escape") { event.preventDefault(); setOpen(false); }
  };
  const submitUrl = () => {
    const value = url.trim();
    if (!/^https?:\/\/\S+$/i.test(value)) return;
    onAddUrl(value);
    setUrl("");
    setUrlOpen(false);
  };

  return (
    <Root ref={rootRef}>
      <Actions>
        <TriggerButton type="button" aria-haspopup="listbox" aria-expanded={open} disabled={disabled}
          onClick={() => setOpen((value) => !value)}>
          <FaImages aria-hidden="true" />
          <span>從講義素材加入</span>
          <FaChevronDown aria-hidden="true" />
        </TriggerButton>
        <SmallButton type="button" aria-pressed={urlOpen} disabled={disabled} onClick={() => setUrlOpen((value) => !value)}>
          <FaLink aria-hidden="true" />圖片 URL
        </SmallButton>
        <SmallButton type="button" disabled={disabled} onClick={onAddCode}>
          <FaCode aria-hidden="true" />Python 圖碼
        </SmallButton>
      </Actions>

      {urlOpen && (
        <UrlRow>
          <input type="url" aria-label="附圖網址" placeholder="https://…/figure.png" value={url} autoFocus
            onChange={(event) => setUrl(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") { event.preventDefault(); submitUrl(); }
              if (event.key === "Escape") setUrlOpen(false);
            }} />
          <SmallButton type="button" disabled={!/^https?:\/\/\S+$/i.test(url.trim())} onClick={submitUrl}>加入</SmallButton>
        </UrlRow>
      )}

      {open && (
        <Popover role="dialog" aria-label="選擇講義素材">
          <ListColumn>
            <input type="search" aria-label="搜尋講義素材" placeholder={`搜尋 ${files.length} 個素材`} value={query} autoFocus
              onChange={(event) => { setQuery(event.target.value); setActiveIndex(-1); }} onKeyDown={handleKeyDown} />
            <List ref={listRef} role="listbox" aria-label="講義素材">
              {matches.length ? matches.map((file, index) => (
                <Option key={file.key} role="option" data-index={index} aria-selected={index === activeIndex}
                  $active={index === activeIndex}
                  onPointerEnter={() => { setActiveIndex(index); schedulePreview(file); }}
                  onPointerLeave={() => window.clearTimeout(hoverTimerRef.current)}
                  onClick={() => pick(file)}>
                  {file.name || file.key}
                </Option>
              )) : <Empty>找不到符合的素材</Empty>}
            </List>
          </ListColumn>
          <PreviewColumn aria-live="polite">
            {previewFile?.url ? (
              <>
                <img key={previewFile.key} src={previewFile.url} alt={previewFile.name || ""} decoding="async" />
                <small title={previewFile.key}>{previewFile.name || previewFile.key}</small>
              </>
            ) : <Empty>滑過素材即可預覽</Empty>}
          </PreviewColumn>
        </Popover>
      )}
    </Root>
  );
}

const Root = styled.div`
  position: relative;
  display: grid;
  gap: 8px;
  min-width: 0;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const buttonBase = `
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 36px;
  border: 1px solid var(--app-border, #cfd4dc);
  border-radius: 8px;
  padding: 0 12px;
  background: var(--app-input-bg, #fff);
  color: var(--app-surface-text, #202124);
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  &:hover:not(:disabled) { background: var(--app-surface-soft, #f4f5f7); }
  &:disabled { opacity: .55; cursor: not-allowed; }
  svg { flex: none; font-size: 12px; }
`;

const TriggerButton = styled.button`
  ${buttonBase}
  flex: 1 1 180px;
  > span { flex: 1; text-align: left; }
`;

const SmallButton = styled.button`
  ${buttonBase}
  &[aria-pressed="true"] { border-color: var(--app-nav-active-bg, #202124); }
`;

const UrlRow = styled.div`
  display: flex;
  gap: 8px;
  > input { flex: 1; min-width: 0; }
`;

const Popover = styled.div`
  position: absolute;
  top: 44px;
  left: 0;
  right: 0;
  z-index: 30;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(140px, 200px);
  gap: 10px;
  max-height: 340px;
  border: 1px solid var(--app-border, #dedede);
  border-radius: 10px;
  padding: 10px;
  background: var(--app-surface, #fff);
  box-shadow: 0 10px 28px rgba(0, 0, 0, .16);

  @media (max-width: 520px) { grid-template-columns: minmax(0, 1fr); }
`;

const ListColumn = styled.div`
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  gap: 8px;
  min-height: 0;
`;

const List = styled.div`
  overflow: auto;
  overscroll-behavior: contain;
  max-height: 270px;
`;

const Option = styled.div`
  padding: 6px 8px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  overflow-wrap: anywhere;
  background: ${({ $active }) => ($active ? "var(--app-surface-soft, #f1f3f4)" : "transparent")};
`;

const PreviewColumn = styled.div`
  display: grid;
  align-content: center;
  justify-items: center;
  gap: 6px;
  min-height: 140px;
  border-radius: 8px;
  padding: 8px;
  background:
    repeating-conic-gradient(var(--app-surface-soft, #f1f3f4) 0 25%, transparent 0 50%) 0 0 / 14px 14px;

  > img { max-width: 100%; max-height: 220px; object-fit: contain; }
  > small {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--app-muted-text, #5f6368);
  }
  @media (max-width: 520px) { display: none; }
`;

const Empty = styled.div`
  padding: 12px 8px;
  color: var(--app-muted-text, #5f6368);
  font-size: 12px;
  text-align: center;
`;
